import { useEffect, useState, useCallback } from "react";
import { AppLayout, RiskBanner } from "@/components/AppLayout";
import BotWorkingAnimation from "@/components/BotWorkingAnimation";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Clock, CheckCircle2, AlertTriangle, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const TIER_LABELS = ["Starter", "Growth", "Pro", "Elite"];
const TIER_COLORS = ["#22C55E", "#3B82F6", "#A855F7", "#EAB308"];

function formatRemaining(endIso) {
  if (!endIso) return null;
  const end = new Date(endIso).getTime();
  const now = Date.now();
  const diff = Math.max(0, end - now);
  const d = Math.floor(diff / (24*3600*1000));
  const h = Math.floor((diff % (24*3600*1000)) / (3600*1000));
  const m = Math.floor((diff % (3600*1000)) / (60*1000));
  return `${d}d ${h}h ${m}m`;
}

export default function Bots() {
  const { user, loading: authLoading, refresh } = useAuth();
  const [bots, setBots] = useState([]);
  const [cycleDays, setCycleDays] = useState(7);
  const [amount, setAmount] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [investments, setInvestments] = useState([]);
  const [source, setSource] = useState("real");
  const [credit, setCredit] = useState(null);
  const [, setTick] = useState(0);

  const loadInvestments = useCallback(async () => {
    try {
      const { data } = await api.get("/bots/my-investments");
      setInvestments(data.investments || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadCredit = useCallback(async () => {
    try {
      const { data } = await api.get("/credit/my");
      setCredit(data.credit_line || null);
    } catch (e) { setCredit(null); }
  }, []);

  useEffect(() => { (async () => {
    const { data } = await api.get("/bots"); setBots(data.bots); setCycleDays(data.cycle_days || 7);
    await Promise.all([loadInvestments(), loadCredit()]);
  })(); }, [loadInvestments, loadCredit]);

  // Auto-select default funding source when opening the dialog
  useEffect(() => {
    if (!confirming) return;
    const realOk = (user?.balance || 0) >= (selected?.min_invest || 0);
    const creditOk = credit && credit.status === "approved" && Number(credit.remaining || 0) >= (selected?.min_invest || 0);
    if (!realOk && creditOk) setSource("credit");
    else setSource("real");
  }, [confirming, user?.balance, credit, selected]);

  // Tick every second so countdowns re-render
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bots/activate", { bot_id: selected.id, amount: parseFloat(amount), source });
      toast.success(data.message || `${selected.name} activated — 7-day cycle started`);
      await refresh();
      await Promise.all([loadInvestments(), loadCredit()]);
      setConfirming(false);
      setAmount("");
      setSource("real");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const running = investments.filter(i => i.status === "running");
  const activeIdsMap = Object.fromEntries(running.map(i => [i.bot_id, i]));
  const totalInvested = running.reduce((s, i) => s + (i.amount || 0), 0);
  const accountActive = user?.account_status === "active";
  const kycOk = user?.kyc_status === "approved";

  return (
    <AppLayout variant="user">
      <RiskBanner />

      {!authLoading && !accountActive && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3 text-amber-200 text-sm" data-testid="account-inactive-banner">
          <AlertTriangle size={18} className="mt-0.5 shrink-0"/>
          <div>
            <strong className="font-semibold">Account Inactive</strong> — Deposit at least <span className="font-mono text-white">${user?.min_activation_amount || 10}</span> to activate your account and unlock bots.
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Trading Engines</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Choose your bots</h1>
        <p className="text-slate-400 mt-2 text-sm max-w-2xl">Run <strong className="text-white">multiple bots concurrently</strong> — each activation launches its own {cycleDays}-day cycle with an independent timer. Daily profits from every active bot credit to your balance.</p>
      </div>

      {running.length > 0 && (
        <div className="mb-6 space-y-4" data-testid="running-cycles-panel">
          <div className="flex items-center gap-2 text-slate-300">
            <Layers size={16} className="text-[#8AE234]"/>
            <div className="text-xs uppercase tracking-widest">
              {running.length} active cycle{running.length>1?"s":""} · Total locked <span className="font-mono text-white">${totalInvested.toFixed(2)}</span> · Total earned <span className="font-mono text-[#8AE234]">+${running.reduce((s,i)=>s+(i.total_profit_earned||0),0).toFixed(2)}</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {running.map((inv) => {
              const b = bots.find(x => x.id === inv.bot_id);
              const earned = Number(inv.total_profit_earned || 0);
              return (
                <div key={inv.id} data-testid={`running-cycle-${inv.bot_id}`} className="rounded-xl border border-[#8AE234]/30 bg-[#8AE234]/5 px-5 py-4 grid grid-cols-4 gap-3 items-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Bot</div>
                    <div className="font-heading text-[#8AE234] text-base">{b?.name || `Bot-${inv.bot_id}`}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#8AE234]/70 mt-0.5 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#8AE234] animate-pulse"/>Scanning</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Invested</div>
                    <div className="font-mono text-white text-base">${(inv.amount||0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Profit</div>
                    <div className="font-mono text-[#8AE234] text-base font-bold">+${earned.toFixed(2)}</div>
                    <div className="text-[9px] text-slate-500">{inv.days_credited || 0}/7 days</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Ends in</div>
                    <div className="font-mono text-nb-cyan text-sm flex items-center gap-1"><Clock size={12}/> {formatRemaining(inv.cycle_end) || "…"}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {running[0] && (() => {
            const b0 = bots.find(x => x.id === running[0].bot_id);
            return <BotWorkingAnimation botId={running[0].bot_id} botName={b0?.name} invested={totalInvested}/>;
          })()}
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {bots.map((b, i) => {
          const isRunning = !!activeIdsMap[b.id];
          const realBalanceOk = (user?.balance || 0) >= (b.min_balance || b.min_invest);
          const creditAvailable = credit && credit.status === "approved" ? Number(credit.remaining || 0) : 0;
          const creditBalanceOk = creditAvailable >= (b.min_invest || 0);
          const balanceOk = realBalanceOk || creditBalanceOk;
          const canActivate = accountActive && kycOk && !isRunning && balanceOk;
          return (
            <div key={b.id} data-testid={`bot-tile-${b.id}`} className={`nb-card p-6 relative ${isRunning?"border-[#8AE234] animate-pulse-neon":""} ${!balanceOk && !isRunning?"opacity-60":""}`}>
              <div className="text-xs uppercase tracking-widest" style={{color: TIER_COLORS[i]}}>{TIER_LABELS[i]}</div>
              <div className="font-heading font-bold text-white text-xl mt-2">{b.name}</div>
              <div className="mt-6">
                <div className="text-xs text-slate-500">Daily Profit Range</div>
                <div className="font-mono text-2xl neon-text">{b.min_pct}% – {b.max_pct}%</div>
                <div className="text-xs text-slate-500 mt-1">Randomised daily · Live profit shown in your ledger</div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-slate-500">Service</div><div className="font-mono text-white">{b.service_charge}%</div></div>
                <div><div className="text-slate-500">Min Invest</div><div className="font-mono text-white">${b.min_invest}</div></div>
                <div><div className="text-slate-500">Min Balance</div><div className="font-mono text-white">${b.min_balance || b.min_invest}</div></div>
                <div><div className="text-slate-500">Cycle</div><div className="font-mono text-white">{cycleDays}d</div></div>
              </div>
              <button
                data-testid={`activate-bot-${b.id}`}
                disabled={!canActivate}
                onClick={()=>{ setSelected(b); setConfirming(true); }}
                className={`mt-5 w-full ${isRunning?"nb-btn-secondary":"nb-btn-primary"} text-sm`}
              >
                {isRunning ? <><CheckCircle2 size={12} className="inline mr-1"/> Running</> : !balanceOk ? <><Lock size={12} className="inline mr-1"/> Locked</> : "Activate"}
              </button>
              {isRunning && <div className="text-[10px] text-[#8AE234]/80 mt-2 text-center">Complete this cycle to re-activate this bot</div>}
              {!accountActive && !isRunning && <div className="text-[10px] text-amber-300/80 mt-2 text-center">Account inactive</div>}
              {accountActive && !kycOk && !isRunning && <div className="text-[10px] text-amber-300/80 mt-2 text-center">KYC approval required</div>}
              {accountActive && kycOk && !balanceOk && !isRunning && <div className="text-[10px] text-slate-500 mt-2 text-center">Balance / credit below ${b.min_balance || b.min_invest}</div>}
            </div>
          );
        })}
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Activate {selected?.name}?</DialogTitle>
            <DialogDescription className="text-slate-400">
              A new {cycleDays}-day cycle will start immediately. This bot runs independently alongside your other active bots.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-400 uppercase tracking-widest">Investment Amount (USDT)</div>
            <input data-testid="activate-amount" type="number" min={selected?.min_invest} value={amount} onChange={(e)=>setAmount(e.target.value)} className="nb-input" placeholder={`Min $${selected?.min_invest}`}/>
            {credit && credit.status === "approved" ? (
              <>
                <div className="text-xs text-slate-400 uppercase tracking-widest mt-2">Funding Source · <span className="normal-case text-slate-500">click a card to auto-fill max</span></div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="source-real"
                    onClick={() => { setSource("real"); const b = Number(user?.balance||0); if (b > 0) setAmount(String(b.toFixed(2))); }}
                    className={`px-3 py-2 rounded-lg border text-xs text-left ${source==="real" ? "border-[#8AE234] bg-[#8AE234]/10 text-[#8AE234]" : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30"}`}
                  >
                    <div className="font-bold">Wallet Balance</div>
                    <div className="font-mono text-[11px] opacity-80">${(user?.balance||0).toFixed(2)} available</div>
                    <div className="text-[10px] opacity-70 mt-0.5">Earns commissions</div>
                  </button>
                  <button
                    data-testid="source-credit"
                    onClick={() => { setSource("credit"); const c = Number(credit.available_for_activation ?? credit.total_capacity ?? credit.remaining ?? 0); if (c > 0) setAmount(String(c.toFixed(2))); }}
                    className={`px-3 py-2 rounded-lg border text-xs text-left ${source==="credit" ? "border-[#EAB308] bg-[#EAB308]/10 text-[#EAB308]" : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30"}`}
                  >
                    <div className="font-bold">Company Credit + Hold Profit</div>
                    <div className="font-mono text-[11px] opacity-80">${Number(credit.available_for_activation ?? credit.total_capacity ?? credit.remaining ?? 0).toFixed(2)} available</div>
                    <div className="text-[10px] opacity-70 mt-0.5">Credit ${Number(credit.amount||0).toFixed(2)} + Hold ${Number(credit.hold_profit||0).toFixed(2)} · re-investable</div>
                  </button>
                </div>
                {source === "credit" && (
                  <div className="text-[11px] rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-200 px-3 py-2">
                    Credit-funded activation earns NO direct/level/leadership/shareholder commissions. Profits are held until credit is fully repaid.
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-slate-500">Available balance: <span className="font-mono text-white">${(user?.balance||0).toFixed(2)}</span></div>
            )}
            <div className="text-xs rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-200 px-3 py-2">Service charge: {selected?.service_charge}% of daily profit. Funds locked for {cycleDays} days — cannot stop early.</div>
          </div>
          <DialogFooter>
            <button onClick={()=>setConfirming(false)} className="nb-btn-secondary text-sm">Cancel</button>
            <button data-testid="confirm-activate" disabled={busy || !amount || parseFloat(amount) < (selected?.min_invest||0)} onClick={activate} className="nb-btn-primary text-sm">{busy?"Activating…":"Confirm & Start"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
